import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment-timezone";

import "moment/dist/locale/en-gb";
import "moment/dist/locale/lt";
import "moment/dist/locale/es";
import "moment/dist/locale/pt";
import "moment/dist/locale/fr";
import "moment/dist/locale/uk";
import "moment/dist/locale/ja";
import "moment/dist/locale/ko";
import "moment/dist/locale/de";
import "moment/dist/locale/it";
import "moment/dist/locale/pl";
import "moment/dist/locale/zh-cn";
import "moment/dist/locale/zh-tw";
import "moment/dist/locale/ru";
import "moment/dist/locale/id";

import {
    deDE,
    enUS,
    esES,
    frFR,
    idID,
    itIT,
    jaJP,
    koKR,
    ltLT,
    plPL,
    ptBR,
    ruRU,
    ukUA,
    zhCN,
    zhTW
} from "i18next/materialUi";

import {
    ENDPOINTS,
    getEnvironment,
    getValidLanguage,
    I18_NEXT_LANG,
    I18_NEXT_LANG_CHANGED,
    LANGUAGES,
    LOCALES,
    TRUE,
    useApi
} from "shared";
import { trackGoogleAnalyticsEvent } from "shared/hooks/useGoogleAnalytics/functions";

import LanguageContext from "./languageContext";
import { LanguageInitialState, LanguageStateProps } from "./types";

const LanguageState = ({ children }: LanguageStateProps) => {
    const { i18n } = useTranslation();
    const { updateData, handleResponse } = useApi();

    const { isProductionEnv } = getEnvironment();

    const getLanguage = () => {
        const localStorageLang = localStorage.getItem(I18_NEXT_LANG);

        return getValidLanguage(localStorageLang, i18n.language as LANGUAGES);
    };

    const state: LanguageInitialState = {
        languages: [
            {
                id: LANGUAGES.English,
                label: "English",
                momentLocale: LOCALES.Lithuanian,
                muiLocale: enUS
            },
            {
                id: LANGUAGES.Lithuanian,
                label: "Lietuvių",
                momentLocale: LOCALES.Lithuanian,
                muiLocale: ltLT
            },
            {
                id: LANGUAGES.Spanish,
                label: "Español",
                momentLocale: LOCALES.Spanish,
                muiLocale: esES
            },
            {
                id: LANGUAGES.Portuguese,
                label: "Português",
                momentLocale: LOCALES.Portuguese,
                muiLocale: ptBR
            },
            {
                id: LANGUAGES.French,
                label: "Français",
                momentLocale: LOCALES.French,
                muiLocale: frFR
            },
            {
                id: LANGUAGES.Ukrainian,
                label: "Українська",
                momentLocale: LOCALES.Ukrainian,
                muiLocale: ukUA
            },
            {
                id: LANGUAGES.Japanese,
                label: "日本語",
                momentLocale: LOCALES.Japanese,
                muiLocale: jaJP
            },
            {
                id: LANGUAGES.Korean,
                label: "한국어",
                momentLocale: LOCALES.Korean,
                muiLocale: koKR
            },
            {
                id: LANGUAGES.German,
                label: "Deutsch",
                momentLocale: LOCALES.German,
                muiLocale: deDE
            },
            {
                id: LANGUAGES.Italian,
                label: "Italiano",
                momentLocale: LOCALES.Italian,
                muiLocale: itIT
            },
            {
                id: LANGUAGES.Polish,
                label: "Polski",
                momentLocale: LOCALES.Polish,
                muiLocale: plPL
            },
            {
                id: LANGUAGES.ChineseSimplified,
                label: "简体中文",
                momentLocale: LOCALES.ChineseSimplified,
                muiLocale: zhCN
            },
            {
                id: LANGUAGES.ChineseTraditional,
                label: "繁體中文",
                momentLocale: LOCALES.ChineseTraditional,
                muiLocale: zhTW
            },
            ...(!isProductionEnv
                ? [
                      {
                          id: LANGUAGES.Indonesian,
                          label: "Bahasa Indonesia",
                          momentLocale: LOCALES.Indonesian,
                          muiLocale: idID
                      }
                  ]
                : []),
            {
                id: LANGUAGES.Russian,
                label: "Русский",
                momentLocale: LOCALES.Russian,
                muiLocale: ruRU
            }
        ],
        language: getLanguage()
    };

    const setMomentLocale = useCallback(
        (newLang: LANGUAGES) => {
            const currentLanguage = state.languages.find(
                lang => lang.id === newLang
            );

            const localeLanguage = currentLanguage
                ? currentLanguage.momentLocale
                : LOCALES.English;

            moment.locale(localeLanguage);
        },
        [state.languages]
    );

    const setNewLanguage = useCallback(
        async (
            lang: LANGUAGES,
            hasApiCall: boolean,
            hasChangedManually: boolean,
            collectAnalytics: boolean
        ) => {
            if (collectAnalytics) {
                trackGoogleAnalyticsEvent("language_change", {
                    language_from: state.language,
                    language_to: lang,
                    language_change_from_to: `${state.language} → ${lang}`
                });
            }

            i18n.changeLanguage(lang as unknown as string);
            setMomentLocale(lang);

            localStorage.setItem(I18_NEXT_LANG, lang as unknown as string);

            if (hasChangedManually) {
                localStorage.setItem(I18_NEXT_LANG_CHANGED, TRUE);
            } else {
                localStorage.removeItem(I18_NEXT_LANG_CHANGED);
            }

            if (hasApiCall) {
                try {
                    const payload = { language: lang };
                    await updateData(ENDPOINTS.UsersSelf, null, payload);
                } catch (error) {
                    handleResponse(error);
                }
            }
        },
        [i18n, state.language, setMomentLocale, updateData, handleResponse]
    );

    const value = useMemo(
        () => ({
            languages: state.languages,
            language: state.language,
            setNewLanguage
        }),
        [state.languages, state.language, setNewLanguage]
    );

    return <LanguageContext value={value}>{children}</LanguageContext>;
};

export default LanguageState;
